import * as Yup from "yup";

import {
  EMAIL_REGEX,
  validatePhoneWithCountrySelection,
  getDefaultCountryCode
} from "@kivunova/kivufrontendcommon";
import { Employee, EmployeeFormData } from "../types/employees";
import { UserRole } from "../../Profile/enums/profileEnums";

import { ROLE_HIERARCHY } from "../constants/employeeRoleHierachy";

export const getAssignableRoles = (
  actorRole: UserRole | undefined
): UserRole[] => {
  if (!actorRole) return [];

  const actorIndex = ROLE_HIERARCHY.indexOf(actorRole);
  if (actorIndex === -1) return [];

  return ROLE_HIERARCHY.slice(actorIndex + 1);
};

export function getDefaultAssignableRole(
  actorRole: UserRole | undefined
): UserRole | undefined {
  const assignableRoles = getAssignableRoles(actorRole);

  if (assignableRoles.length === 0) return undefined;

  return assignableRoles[assignableRoles.length - 1];
}

export const buildEmployeeSchema = (
  assignableRoles: UserRole[],
  t: (key: string, fallback: string) => string
) => {
  return Yup.object<EmployeeFormData>().shape({
    firstName: Yup.string().required(
      t("first-name-required", "First name is required")
    ),
    lastName: Yup.string().required(
      t("last-name-required", "Last name is required")
    ),
    email: Yup.string()
      .required(t("email-empty-error", "Email is required"))
      .matches(EMAIL_REGEX, t("email-invalid-error", "Invalid email format")),
    phone: Yup.string()
      .required(t("phone-required", "Phone number is required"))
      .test(
        "phone-validation",
        t("phone-invalid", "Invalid phone number for selected country"),
        function (value) {
          const { phoneCountryCode } = this.parent;
          return validatePhoneWithCountrySelection(value, phoneCountryCode);
        }
      ),
    phoneCountryCode: Yup.string().required(
      t("country-code-required", "Country code is required")
    ),
    profileImage: Yup.string().optional(),
    companyId: Yup.string().required(
      t("company-id-required", "Company ID is required")
    ),
    storeId: Yup.string().required(
      t("store-id-required", "Store ID is required")
    ),
    role: Yup.mixed<UserRole>()
      .oneOf(assignableRoles, t("role-invalid", "Invalid role"))
      .required(t("role-required", "Role is required"))
  });
};

export const buildDefaults = (
  userDetails: Employee,
  storeId?: string
): EmployeeFormData => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneCountryCode: getDefaultCountryCode(),
  profileImage: "",
  companyId: userDetails?.companyId || "",
  storeId: storeId || "",
  role: (getDefaultAssignableRole(userDetails?.role as UserRole | undefined) ||
    "") as UserRole
});
